import React, { createContext, useEffect, useMemo, useReducer } from 'react';

const initialState = {
    package: null,
    workshops: [],
    sympoPrice: 0,
    totalPrice: 0,
    totalWorkshopPrice: 0,
    bundleDiscount: 0,
    invoiceId: null,
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_PACKAGE':
            return { ...state, package: action.payload };

        case 'ADD_WORKSHOP':
            return {
                ...state,
                workshops: [...state.workshops, action.payload],
            };

        case 'REMOVE_WORKSHOP':
            return {
                ...state,
                workshops: state.workshops.filter(
                    (w) => w.id !== (action.payload.id || action.payload),
                ),
            };

        case 'CLEAR':
            return { ...initialState };

        case 'SET_TOTAL':
            return { ...state, totalPrice: action.payload };

        case 'SET_BUNDLE_DISCOUNT':
            return { ...state, bundleDiscount: action.payload };

        case 'SET_TOTAL_WORKSHOP_PRICE':
            return { ...state, totalWorkshopPrice: action.payload };

        case 'SET_SYMPO_PRICE':
            return { ...state, sympoPrice: action.payload };

        case 'SET_INVOICE':
            return {
                ...state,
                package: action.payload?.package || null,
                workshops: (action.payload?.workshop_details || []).map(
                    (d) => d.workshop,
                ),
                totalPrice: action.payload?.total_price || 0,
                totalWorkshopPrice: action.payload?.total_workshop_price || 0,
                bundleDiscount: action.payload?.bundle_discount || 0,
                invoiceId: action.payload?.id || null,
            };

        default:
            return state;
    }
};

const CartContext = createContext({ ...initialState });

export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState, (init) => {
        try {
            const saved = localStorage.getItem('inaacc_cart_v1');
            return saved ? JSON.parse(saved) : init;
        } catch (_) {
            return init;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('inaacc_cart_v1', JSON.stringify(state));
        } catch (_) {}
    }, [state]);

    const setPackage = (pkg) => dispatch({ type: 'SET_PACKAGE', payload: pkg });
    const addWorkshop = (ws) => dispatch({ type: 'ADD_WORKSHOP', payload: ws });
    const removeWorkshop = (ws) =>
        dispatch({ type: 'REMOVE_WORKSHOP', payload: ws });
    const clearCart = () => dispatch({ type: 'CLEAR' });
    const setTotal = (total) => dispatch({ type: 'SET_TOTAL', payload: total });
    const setBundleDiscount = (discount) =>
        dispatch({ type: 'SET_BUNDLE_DISCOUNT', payload: discount });
    const setFromInvoice = (invoice) =>
        dispatch({ type: 'SET_INVOICE', payload: invoice });
    const setSympoPrice = (price) =>
        dispatch({ type: 'SET_SYMPO_PRICE', payload: price });
    const setTotalWorkshopPrice = (price) =>
        dispatch({ type: 'SET_TOTAL_WORKSHOP_PRICE', payload: price });

    const value = useMemo(
        () => ({
            ...state,
            setPackage,
            addWorkshop,
            removeWorkshop,
            clearCart,
            setTotal,
            setFromInvoice,
            setSympoPrice,
            setBundleDiscount,
            setTotalWorkshopPrice,
        }),
        [state],
    );

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
};

export const useCart = () => React.useContext(CartContext);

export default CartContext;
